import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileTextIcon, SaveIcon, XIcon } from "lucide-react";

import { cn } from "@/libs/cn";
import { Button, FileInput, Input, PageBreadcrumb, SelectV2, Spinner } from "@/modules/common/components";
import { inputClassNameValues } from "@/modules/common/components/files/Input";
import useAdminUsersOptions from "@/modules/common/hooks/lists/useAdminUsersOptions";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import useToastOnError from "@/modules/common/hooks/useToastOnError";
import { LEAD_ORIGIN_OPTIONS, LEAD_SEGMENT_OPTIONS, LEAD_STATUS_OPTIONS } from "@/modules/common/types/Leads.enum";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";
import { digitsOnly, formatPhoneNumber } from "@/modules/common/utils/format";
import { uploadFiles } from "@/modules/common/utils/upload";
import { ROUTES } from "@/routes/constants/routes";
import { LeadResponse } from "@/service/response/admin-leads";

import useLeadDetails from "../../hooks/useLeadDetails";
import useUpdateLead from "../../hooks/useUpdateLead";
import { LeadEditFormData, leadEditSchema } from "../../schemas";

const ACCEPTED_FILES = "image/*,.pdf,.doc,.docx,.xls,.xlsx";

const getDefaultValues = (lead: LeadResponse): LeadEditFormData => ({
  name: lead.name,
  company: lead.company,
  email: lead.email,
  phoneNumber: lead.phoneNumber ? formatPhoneNumber(lead.phoneNumber) : "",
  segment: lead.segment ?? "",
  originType: lead.originType,
  interest: lead.interest ?? "",
  status: lead.status,
  responsibleById: lead.responsibleBy?.id ?? null,
  observation: "",
});

const LeadEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const detailsRoute = generatePath(ROUTES.LEADS.DETAILS, { id: id! });
  const navigateBack = useNavigateBack(detailsRoute);

  const { data: lead, isLoading, isError, error } = useLeadDetails({ id: id!, enabled: !!id });
  const updateLead = useUpdateLead();
  const [files, setFiles] = useState<File[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const defaultValues = useMemo(() => (lead ? getDefaultValues(lead) : undefined), [lead]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<LeadEditFormData>({ resolver: zodResolver(leadEditSchema), defaultValues });

  const attachmentsChanged = files.length > 0 || removedAttachmentIds.length > 0;

  useEffect(() => {
    if (!defaultValues) return;
    if (isDirty || attachmentsChanged) return;

    reset(defaultValues);
  }, [defaultValues, isDirty, attachmentsChanged, reset]);

  const responsibleOptions = useAdminUsersOptions(
    lead?.responsibleBy ? { value: lead.responsibleBy.id, label: lead.responsibleBy.name } : undefined,
  );

  const navigateToList = useCallback(() => navigate(ROUTES.LEADS.LIST), [navigate]);

  useToastOnError(isError, error, "Erro ao carregar lead.", navigateToList);

  const isSaving = updateLead.isPending || isUploading;
  const disabled = isSaving;
  const keptAttachments = lead?.attachments.filter((attachment) => !removedAttachmentIds.includes(attachment.id)) ?? [];

  const onSubmit = async (values: LeadEditFormData) => {
    if (!lead) return;

    let attachmentIds: string[] | undefined;

    if (attachmentsChanged) {
      let uploadedIds: string[] = [];

      if (files.length) {
        setIsUploading(true);
        setUploadedCount(0);

        try {
          uploadedIds = await uploadFiles(files, { onProgress: setUploadedCount });
        } catch (error) {
          toast.error(getApiErrorMessage(error, "Erro ao enviar a fatura. Tente novamente."));
          return;
        } finally {
          setIsUploading(false);
        }
      }

      attachmentIds = [...keptAttachments.map((attachment) => attachment.id), ...uploadedIds];
    }

    const data = {
      ...(dirtyFields.name && { name: values.name }),
      ...(dirtyFields.company && { company: values.company }),
      ...(dirtyFields.email && { email: values.email }),
      ...(dirtyFields.phoneNumber && { phoneNumber: values.phoneNumber ? digitsOnly(values.phoneNumber) : null }),
      ...(dirtyFields.segment && { segment: values.segment || null }),
      ...(dirtyFields.interest && { interest: values.interest || null }),
      ...(dirtyFields.status && { status: values.status }),
      ...(dirtyFields.responsibleById && { responsibleById: values.responsibleById ?? null }),
      ...(dirtyFields.originType && { originType: values.originType ?? null }),
      ...(values.observation && { observation: values.observation }),
      attachmentIds,
    };

    await updateLead.mutateAsync({
      id: lead.id,
      data,
    });

    toast.success("Alterações salvas com sucesso!");
    navigate(detailsRoute);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb
        items={[{ label: "Leads", to: ROUTES.LEADS.LIST }, ...(lead ? [{ label: lead.name, to: detailsRoute }] : []), { label: "Editar" }]}
      />

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : lead ? (
        <>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col">
                <p className="text-[22px] font-bold text-ui-primary">Editar lead</p>
                <p className="text-[14px] text-ui-text-muted">Atualize os dados de contato e o acompanhamento comercial.</p>
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outlined"
                  label="Cancelar"
                  className="w-full sm:w-auto"
                  disabled={isSaving}
                  onClick={navigateBack}
                />
                <Button
                  type="submit"
                  label={isUploading ? `Enviando arquivos (${uploadedCount}/${files.length})` : "Salvar alterações"}
                  icon={<SaveIcon />}
                  className="w-full sm:w-auto"
                  loading={isSaving}
                  disabled={(!isDirty && !attachmentsChanged) || isSaving}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
              <div className="flex flex-col">
                <p className="text-[16px] font-bold text-ui-primary">Dados do lead</p>
                <p className="text-[12px] text-ui-text-muted">Informações de contato da empresa.</p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Input label="Nome" disabled={disabled} {...register("name")} />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
                </div>

                <div>
                  <Input label="Empresa" disabled={disabled} {...register("company")} />
                  {errors.company && <p className="mt-1 text-xs text-red-500">{errors.company.message}</p>}
                </div>

                <div>
                  <Input label="E-mail" maskType="email" disabled={disabled} {...register("email")} />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
                </div>

                <div>
                  <Input label="Telefone / WhatsApp" maskType="tel" disabled={disabled} {...register("phoneNumber")} />
                  {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber.message}</p>}
                </div>

                <Controller
                  name="segment"
                  control={control}
                  render={({ field }) => (
                    <SelectV2
                      mode="single"
                      label="Segmento"
                      placeholder="Selecione o segmento"
                      options={LEAD_SEGMENT_OPTIONS}
                      value={field.value || undefined}
                      disabled={disabled}
                      onChange={(next) => field.onChange(next ?? "")}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
              <div className="flex flex-col">
                <p className="text-[16px] font-bold text-ui-primary">Solicitação</p>
                <p className="text-[12px] text-ui-text-muted">Origem do lead e necessidade informada.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Controller
                    name="originType"
                    control={control}
                    render={({ field }) => (
                      <SelectV2
                        mode="single"
                        label="Origem"
                        placeholder="Selecione a origem"
                        options={LEAD_ORIGIN_OPTIONS}
                        value={field.value || undefined}
                        onChange={(next) => field.onChange(next ?? "")}
                      />
                    )}
                  />
                  {errors.originType && <p className="mt-1 text-xs text-red-500">{errors.originType.message}</p>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm">Necessidade informada</label>
                <textarea
                  rows={3}
                  disabled={disabled}
                  placeholder="O que a empresa informou ou precisa..."
                  className={cn(inputClassNameValues, "h-auto resize-y")}
                  {...register("interest")}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
              <div className="flex flex-col">
                <p className="text-[16px] font-bold text-ui-primary">Fatura de energia</p>
                <p className="text-[12px] text-ui-text-muted">Arquivos anexados pelo lead ou pela equipe.</p>
              </div>
              {keptAttachments.length > 0 && (
                <div className="flex flex-col gap-2">
                  {keptAttachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center gap-2 rounded-lg border border-ui-border p-3">
                      <FileTextIcon className="h-5 w-5 shrink-0 text-ui-text-muted" />
                      <span className="min-w-0 flex-1 truncate text-sm text-ui-text">{attachment.fileName ?? "Anexo"}</span>
                      <Button
                        type="button"
                        variant="outlined"
                        label="Remover"
                        icon={<XIcon size={16} />}
                        disabled={disabled}
                        onClick={() => setRemovedAttachmentIds((current) => [...current, attachment.id])}
                      />
                    </div>
                  ))}
                </div>
              )}
              <FileInput
                title="Clique para anexar a fatura"
                files={files}
                onChange={setFiles}
                accept={ACCEPTED_FILES}
                multiple
                disabled={disabled}
              />
            </div>

            <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
              <div className="flex flex-col">
                <p className="text-[16px] font-bold text-ui-primary">Acompanhamento comercial</p>
                <p className="text-[12px] text-ui-text-muted">Atualize o status, o responsável e registre observações do atendimento.</p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <SelectV2
                        mode="single"
                        label="Status"
                        options={LEAD_STATUS_OPTIONS}
                        value={field.value}
                        disabled={disabled}
                        onChange={(next) => field.onChange(next)}
                      />
                    )}
                  />
                  {errors.status && <p className="mt-1 text-xs text-red-500">{errors.status.message}</p>}
                </div>

                <Controller
                  name="responsibleById"
                  control={control}
                  render={({ field }) => (
                    <SelectV2
                      mode="single"
                      label="Responsável"
                      placeholder="Não atribuído"
                      options={responsibleOptions.options}
                      isLoading={responsibleOptions.isLoading}
                      hasMore={responsibleOptions.hasMore}
                      onLoadMore={responsibleOptions.onLoadMore}
                      onSearch={responsibleOptions.onSearch}
                      value={field.value ?? undefined}
                      disabled={disabled}
                      onChange={(next) => field.onChange(next ?? null)}
                    />
                  )}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold">Observações</label>
                <label className="text-xs text-ui-text-muted font-medium">Adicione uma nota ao histórico do lead</label>

                <textarea
                  rows={3}
                  disabled={disabled}
                  placeholder="Registre aqui informações do atendimento..."
                  className={cn(inputClassNameValues, "h-auto resize-y")}
                  {...register("observation")}
                />
              </div>
            </div>
          </form>
        </>
      ) : null}
    </div>
  );
};

export default LeadEdit;
